import { useEffect, useMemo, useState } from "react";
import { StreamingStateContext } from ".";
import { useVideoPlayerRefs } from "../VideoPlayerRefs";
import { StreamingState } from "../../types/streamingState";

export interface StreamingStateProps {
  children?: React.ReactNode;
  streamingStateCallback?: (streamingState: StreamingState) => void;
  src?: string;
}

const streamingEvents = [
  {
    name: "loadeddata",
    streamingState: StreamingState.loaded,
  },
  {
    name: "play",
    streamingState: StreamingState.playing,
  },
  { name: "playing", streamingState: StreamingState.playing },
  { name: "pause", streamingState: StreamingState.paused },
  { name: "ended", streamingState: StreamingState.ended },
  { name: "waiting", streamingState: StreamingState.waiting },
];

export const StreamingStateProvider = ({
  children,
  streamingStateCallback,
  src,
}: StreamingStateProps): JSX.Element => {
  const { video } = useVideoPlayerRefs();

  const [streamingState, setStreamingState] = useState<StreamingState>(
    StreamingState.loading
  );

  useEffect(() => {
    setStreamingState(StreamingState.loading);
  }, [src]);

  useEffect(() => {
    if (video.current !== null) {
      const element = video.current;

      const handlers = streamingEvents.map(
        ({ streamingState: newStreamingState }) =>
          () => {
            if (streamingStateCallback !== undefined) {
              streamingStateCallback(newStreamingState);
            }

            setStreamingState(newStreamingState);
          }
      );

      streamingEvents.forEach((event, i) => {
        element.addEventListener(event.name, handlers[i]);
      });

      return () => {
        streamingEvents.forEach((event, i) => {
          element.removeEventListener(event.name, handlers[i]);
        });
      };
    }
  }, [setStreamingState, streamingStateCallback, video]);

  const value = useMemo(() => ({ streamingState }), [streamingState]);

  return (
    <StreamingStateContext.Provider value={value}>
      {children}
    </StreamingStateContext.Provider>
  );
};
